import { useEffect, useId, useRef, useState } from 'react'
import AOS from 'aos'
import Button from '@ui/Button'
import MetaInfo from '@/components/common/MetaInfo'
import { Link } from 'react-router-dom'
import Breadcrumb from '@common/Breadcrumb'
import TransformBanner from '@sections/TransformBanner'
import { ROUTES,SITE } from '@/constants'
import { metaTitleFor } from '../assets/js/navBreadcrumb'
const { label, pageDesc, metaDesc } = metaTitleFor(ROUTES.SOFTWARE_CONSULTING)
import { usePrefersReducedMotion } from '@helpers/usePrefersReducedMotion'

// ── Section 1 — What We Offer cards ──────────────────────────────────
const OFFER_CARDS = [
  { icon: 'fa-solid fa-map',                    title: 'Technology Strategy & Roadmapping',     desc: 'Define technology priorities, identify opportunities, and create a practical roadmap aligned with your business objectives.' },
  { icon: 'fa-solid fa-layer-group',            title: 'Software Architecture Consulting',      desc: 'Design scalable and maintainable application architectures that support current requirements and future growth.' },
  { icon: 'fa-solid fa-magnifying-glass-chart', title: 'Technology Stack Evaluation',           desc: 'Assess technologies, frameworks, platforms, and tools to help you make informed technology choices.' },
  { icon: 'fa-solid fa-rotate',                 title: 'Application Modernization Consulting',  desc: 'Identify opportunities to modernize legacy applications, architectures, databases, and technology environments.' },
  { icon: 'fa-solid fa-cloud',                  title: 'Cloud Consulting',                      desc: 'Evaluate cloud strategies, application architectures, migration approaches, and cloud-native opportunities.' },
  { icon: 'fa-solid fa-robot',                  title: 'AI & Emerging Technology Consulting',   desc: 'Identify practical opportunities to apply AI, automation, data, and other emerging technologies to business processes and digital products.' },
  { icon: 'fa-solid fa-lightbulb',              title: 'Software Product Consulting',           desc: 'Transform product ideas into practical technology roadmaps, architecture plans, and development strategies.' },
  { icon: 'fa-solid fa-shield-halved',          title: 'Technical Due Diligence',              desc: 'Assess software architecture, technology choices, code quality, scalability, security, and technical risks to support informed business decisions.' },
]

// ── Section 2 — Process steps ─────────────────────────────────────────
const SOFTWARE_CONSULTING_PROCESS_STEPS = [
  { num: '01', icon: 'fa-solid fa-comments',               title: 'Understand', desc: 'We begin by understanding your business objectives, users, existing technology environment, challenges, and future requirements.' },
  { num: '02', icon: 'fa-solid fa-magnifying-glass-chart', title: 'Assess',     desc: 'Our consultants evaluate your current applications, architecture, technology stack, processes, and potential technology gaps.' },
  { num: '03', icon: 'fa-solid fa-lightbulb',              title: 'Recommend',  desc: 'We identify suitable approaches and provide technology recommendations based on business priorities, technical requirements, scalability, security, and cost considerations.' },
  { num: '04', icon: 'fa-solid fa-route',                  title: 'Roadmap',    desc: 'We translate recommendations into a practical technology roadmap with priorities, dependencies, and potential implementation stages.' },
  { num: '05', icon: 'fa-solid fa-rocket',                 title: 'Enable',     desc: 'Where required, our engineering teams can work alongside your organization to help translate the recommended strategy into a working software solution.' },
]

// ── Section 3 — Business Benefits cards ──────────────────────────────
const BENEFIT_CARDS = [
  { icon: 'fa-solid fa-shield-halved',      title: 'Reduce Technology Risk',         desc: 'Make technology decisions based on structured analysis and technical expertise, helping reduce avoidable architectural, development, security, and scalability risks.' },
  { icon: 'fa-solid fa-arrows-up-to-line',  title: 'Improve Scalability',            desc: 'Establish software foundations that can evolve with your users, data, integrations, and business requirements.' },
  { icon: 'fa-solid fa-coins',              title: 'Optimize Technology Investment', desc: 'Select technologies and approaches that balance functionality, performance, scalability, maintainability, and long-term costs.' },
  { icon: 'fa-solid fa-bolt',               title: 'Accelerate Decision-Making',     desc: 'Bring clarity to complex technology choices and move from ideas and challenges to actionable technology plans faster.' },
  { icon: 'fa-solid fa-gauge-high',         title: 'Improve Software Performance',   desc: 'Identify architectural, infrastructure, and application-level improvements that can enhance reliability, performance, and overall system efficiency.' },
  { icon: 'fa-solid fa-seedling',           title: 'Prepare for Future Growth',      desc: 'Build technology strategies that accommodate changing business needs, emerging technologies, evolving customer expectations, and future product expansion.' },
]
// ── Section 2 — Zig-zag roadmap infographic ──────────────────────────
function ProcessFlow({ steps, label }) {
  const flowRef = useRef(null)
  const listRef = useRef(null)
  const svgRef = useRef(null)
  const [layout, setLayout] = useState({ start: null, segments: [] })
  // const [reducedMotion] = useState(
  //   () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  // )

    const reducedMotion = usePrefersReducedMotion()
    
  const arrowId = `process-arrow-${useId().replace(/:/g, '')}`

  // One path per step-to-step segment, so the dot can travel along each one
  useEffect(() => {
    const flow = flowRef.current
    const list = listRef.current
    if (!flow || !list) return

    const draw = () => {
      const icons = list.querySelectorAll('.process-step__icon')
      if (!icons.length) return

      const flowRect = flow.getBoundingClientRect()
      const points = Array.from(icons, (icon) => {
        const rect = icon.getBoundingClientRect()
        return {
          x: rect.left - flowRect.left + rect.width / 2,
          y: rect.top - flowRect.top + rect.height / 2,
        }
      })

      // Vertical stack on mobile is detected from CSS, so no breakpoint is duplicated here
      const isVertical = getComputedStyle(list).flexDirection === 'column'

      // Stop the arrow at the edge of the last icon so the arrowhead stays visible
      const radius = icons[icons.length - 1].offsetWidth / 2
      const last = points[points.length - 1]
      if (isVertical) last.y -= radius
      else last.x -= radius

      const segments = []
      for (let i = 1; i < points.length; i += 1) {
        const cur = points[i - 1]
        const next = points[i]
        const cx = (cur.x + next.x) / 2
        segments.push(
          isVertical
            ? `M ${cur.x} ${cur.y} L ${next.x} ${next.y}`
            : `M ${cur.x} ${cur.y} C ${cx},${cur.y} ${cx},${next.y} ${next.x},${next.y}`
        )
      }
      setLayout({ start: points[0], segments })
    }

    const observer = new ResizeObserver(draw)
    observer.observe(flow)
    document.fonts?.ready.then(draw)

    return () => observer.disconnect()
  }, [steps])

  // Travelling dot — moves along each segment in turn, then loops
  useEffect(() => {
    const svg = svgRef.current
    const dot = svg?.querySelector('.process-svg__dot')
    const paths = svg ? Array.from(svg.querySelectorAll('.process-svg__path')) : []
    if (!dot || !paths.length) return

  const DURATION = 900
let frame = 0
let index = 0
let phaseStart = null

const moveDot = ({ x, y }) => {
  dot.setAttribute('cx', x)
  dot.setAttribute('cy', y)
}

const run = (now) => {
  if (phaseStart === null) phaseStart = now

  const path = paths[index]
  const length = path.getTotalLength()
  const elapsed = now - phaseStart

  if (elapsed < DURATION) {
   const t = elapsed / DURATION

moveDot(path.getPointAtLength(t * length))
  } else {
    index = (index + 1) % paths.length
    phaseStart = now

    const nextPath = paths[index]
    moveDot(nextPath.getPointAtLength(0))
  }

  frame = requestAnimationFrame(run)
}

    const start = () => { if (!frame) frame = requestAnimationFrame(run) }
    const stop = () => {
      cancelAnimationFrame(frame)
      frame = 0
      phaseStart = null
    }

    // Only animate while the roadmap is on screen
    const observer = new IntersectionObserver(([entry]) =>
      entry.isIntersecting ? start() : stop()
    )
    observer.observe(flowRef.current)

    return () => {
      observer.disconnect()
      stop()
    }
  }, [layout.segments])

  return (
    <div className="process-flow" ref={flowRef}>
      <svg className="process-svg" ref={svgRef} aria-hidden="true" focusable="false">
        <defs>
          <marker id={arrowId} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon className="process-svg__arrow" points="0,0 6,3 0,6" />
          </marker>
        </defs>
        {layout.segments.map((d, i) => (
          <path
            key={i}
            className="process-svg__path"
            d={d}
            markerEnd={i === layout.segments.length - 1 ? `url(#${arrowId})` : undefined}
          />
        ))}
        {!reducedMotion && layout.start && (
          <circle className="process-svg__dot" r="5" cx={layout.start.x} cy={layout.start.y} />
        )}
      </svg>

      <ol className="process-flow__list" ref={listRef} aria-label={label}>
        {steps.map((step, i) => (
          <li
            key={step.title}
            className={`process-step process-step--${i % 2 === 0 ? 'top' : 'bottom'}`}
            data-aos="fade"
            data-aos-delay={i * 100}
          >
            <span className="process-step__icon" aria-hidden="true">
              <i className={step.icon} />
            </span>
            <div>
              <h6>{step.title}</h6>
              {step.desc && <p className="process-step__desc">{step.desc}</p>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}
export default function SoftwareConsulting() {
  useEffect(() => { AOS.refresh(); window.scrollTo(0, 0) }, [])

  return (
      <>
       {/* <MetaInfo
  title="Software Consulting | CruxBytes Consultancy Services DEEPTI"
  description="Software consulting from CruxBytes: technology strategy, architecture design, stack evaluation, cloud and AI consulting to help you build scalable software."
  keywords="software consulting, technology consulting, software architecture consulting, technology strategy, technology roadmap, technology stack evaluation, application modernization consulting, cloud consulting, AI consulting, technical due diligence, software product consulting, software consulting company in Thane, software consulting company in Mumbai, software consulting India"
/> */}

<MetaInfo
    title={`${label} ${SITE.metaTagPostText}`} 
  description={metaDesc}
 keywords=""
/>
        <main id="main-content" className="page-wrapper">
         <div className="page-hero">
           <div className="bg-grid bg-grid--fill" aria-hidden="true" />
           <div className="container">
             {/* <div className="section-label">- Application Consulting -</div> */}
             <Breadcrumb />
             <h1 data-aos="fade-up" data-aos-delay={100}>{label}</h1>
             <p className="page-subtitle" data-aos="fade-up" data-aos-delay={200}>
             {pageDesc}
             </p>
            
             <div className="inner-page-actions">
               <Button  href={ROUTES.SALES_ENQUIRY}  variant="primary" icon="fa-arrow-right" iconPos="right" data-aos="fade-up" data-aos-delay={300}>
                 Get a Free Quote
               </Button>
              
             </div>
           </div>
         </div>
     

{/* section 1 */}
        <section className="bg-linear-gradient-light section-py" >
      <div className="container">
           

        {/* Row 2 — */}
         <p className="text-center" data-aos="fade-down">
             Technology decisions can have a lasting impact on your business. The right architecture, technology stack, and development strategy can improve efficiency, scalability, security, and long-term technology costs.
            </p>
        <div className="sc-row" data-aos="fade-up">
         
            
           
           
             <div className="sc-col-text">
            <p >
              CruxBytes provides <span className="text-bold">strategic software consulting services</span> to help businesses make informed technology decisions and build a strong foundation for digital growth. We combine business understanding, software engineering expertise, and knowledge of modern technologies to address complex technology challenges and identify practical opportunities for improvement.
            </p></div> <div className="sc-col-text">
            <p>
              Whether you are planning a new application, modernizing an existing system, evaluating technologies, or looking to improve your software architecture, our consultants help you move forward with greater clarity and confidence.
            </p>
          </div>
        </div>
        <div className="text-center py-4" data-aos="fade-right">
          <div className='text-highlight-light'>
           "We Help You To Make Better Technology Decisions. Build Software That Scales."
          </div>
        </div>  
             <div className="wcs-grid wcs-grid--3 pt-4">
          {OFFER_CARDS.map((card, i) => (
            <div
              key={card.title}
              className="wcs-card-offer"
              data-aos="fade-left"
              data-aos-delay={(i % 3) * 80}
            >
              {/* <i className={`${card.icon} wcs-icon`} aria-hidden="true" /> */}
              <h3 className="wcs-offer-title">{card.title}</h3>
              <p className="wcs-offer-desc">{card.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

{/* section 2 */}
    <section className="bgs-section bgs-section--tint section-py text-center" aria-labelledby="bgs-reach-heading2">
      <div className="container">
        <div data-aos="fade-up">
          <h2 id="bgs-reach-heading2">
           Our Approach to Software Consulting
          </h2>
        </div>     

        {/* Row 2 — */}
        <div className="bgs-row bgs-row--text-only" data-aos="fade-up">
          <div className="bgs-row-content">           
           
            <p >
             We believe effective consulting should result in <span className='text-bold'>clear decisions and actionable outcomes</span>, not lengthy recommendations that remain on paper.
            </p>           
           
          </div>
        </div>
     <ProcessFlow
          steps={SOFTWARE_CONSULTING_PROCESS_STEPS}
          label="Our software consulting approach"
        />
      </div>
      <p className='text-highlight' data-aos="fade-right">Have a technology challenge, a software idea, or an application that needs to evolve? Talk to our software consulting team and discover a practical technology path for your business.

</p>
<div className='pt-4'>
<Button  href={ROUTES.SALES_ENQUIRY}  variant="primary" icon="fa-arrow-right" iconPos="right" data-aos="fade-up">Get Started</Button>
</div>
    </section>
{/* section 3 */}
    <section className="section-py bg-peach" aria-labelledby="pux-outcomes-heading">
  <div className="container">
    <div className="sc-ai-row sc-tm-row--sticky-heading">

      <div className="sc-tm-content" data-aos="fade-left">
         {/* <h2 id="pux-outcomes-heading" >
        Business Benefits of Software Consulting
        </h2> */}
     <h2 id="impact-heading" >
            <span className="impact-heading-dark">Business Benefits of</span>
            <span className="impact-heading-light">Software Consulting</span>
          </h2>
      </div>
      <div  data-aos="fade-right">
        <div className="wcs-grid wcs-grid--3">
          {BENEFIT_CARDS.map((card, i) => (
            <div
              key={card.title}
              className="wcs-card wcs-card--static bg-white"
              data-aos="fade-up"
              data-aos-delay={(i % 3) * 80}
            >
              {/* <i className={`${card.icon} wcs-icon`} aria-hidden="true" /> */}
              <h3 className="wcs-title">{card.title}</h3>
              <p className="wcs-desc text-center">{card.desc}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  </div>
</section>
{/* section 4 */}
    <section className=" section-py" aria-labelledby="bgs-reach-heading3">
      <div className="container">
        <div className="text-center" data-aos="fade-up">
          <h2 id="bgs-reach-heading3 my-6">
           Why Choose CruxBytes for Software Consulting?
          </h2>
        </div>     

        {/* Row 2 — */}
        <div className="bgs-row bgs-row--text-only" data-aos="fade-up">
          <div className="sc-v3-row">           
           
            <p className="sc-v3-col" >
             At CruxBytes, our consulting perspective is backed by hands-on software engineering experience. We understand that technology recommendations need to work in the real world—not just look good on a strategy document.
            </p>
            <p className="sc-v3-col">
              Our teams bring experience across <span className='text-bold'>software development, cloud technologies, AI, data, application modernization, quality engineering, DevOps, and ongoing application support</span>. This allows us to look at technology decisions from both a strategic and implementation perspective.
            </p>
            <p className="sc-v3-col">
              Whether you need an independent technology assessment, guidance for a new digital initiative, or a roadmap for modernizing an existing application, we can help you identify the right path forward.
            </p>
          </div>
        </div>
      <div>      
      </div>
      </div>
    </section>
        
        <TransformBanner />
         </main>
      </>
  )
}